import { useState } from "react";
import { toast } from "sonner";
import { Download, X, Smartphone, Apple } from "lucide-react";
import usePwaInstall from "@/hooks/usePwaInstall";
import { Link } from "react-router-dom";

/**
 * PwaInstallBanner — floating bottom-right banner.
 * - Chromium/Android/Desktop: mostra "Installa app" con native prompt
 * - iOS Safari: mostra istruzioni manuali "Aggiungi a schermata Home"
 * - Nascosto se: installato, dismesso recentemente, o né canInstall né iOS Safari
 */
export default function PwaInstallBanner() {
  const { canInstall, installed, dismissedRecent, isIosSafari, install, dismiss } = usePwaInstall();
  const [busy, setBusy] = useState(false);

  const show = !installed && !dismissedRecent && (canInstall || isIosSafari);
  if (!show) return null;

  const onInstall = async () => {
    setBusy(true);
    try {
      const ok = await install();
      if (ok) toast.success("IH AuditFlow installato correttamente");
      else toast.info("Installazione annullata");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 right-4 z-[60] max-w-[340px] rounded-2xl shadow-xl border border-lavender-300 dark:border-lavender-800 bg-white dark:bg-slate-900 p-4 pr-3 flex items-start gap-3 ih-anim-in"
    >
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#a78bfa] to-[#6d28d9] flex items-center justify-center flex-shrink-0">
        {isIosSafari ? <Apple className="h-5 w-5 text-white" /> : <Smartphone className="h-5 w-5 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug">
          {isIosSafari ? "Installa IH AuditFlow su iPhone" : "Installa IH AuditFlow come app"}
        </div>
        {isIosSafari ? (
          <p className="text-xs text-muted-foreground mt-1">
            Tocca <span className="inline-block px-1.5 py-0.5 rounded bg-accent/50 text-[10px]">Condividi</span> → <strong>Aggiungi a schermata Home</strong>.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">Apri l'app da schermata Home, senza browser.</p>
        )}
        <div className="mt-2 flex items-center gap-2">
          {canInstall ? (
            <button
              data-testid="pwa-install-btn"
              disabled={busy}
              onClick={onInstall}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-lavender-500 hover:bg-lavender-600 text-white"
            >
              <Download className="h-3.5 w-3.5" /> Installa
            </button>
          ) : (
            <Link
              to="/installa"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border border-lavender-300 text-lavender-700 dark:text-lavender-200 hover:bg-lavender-50 dark:hover:bg-lavender-950/40"
            >
              Come si installa
            </Link>
          )}
          <button
            data-testid="pwa-dismiss-btn"
            onClick={dismiss}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Non ora
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Chiudi">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
